import { useEffect, useState } from 'react';
import { api } from '@/lib/api';
import { Card } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { Skeleton } from '@/components/ui/skeleton';
import TrialCard from '@/components/TrialCard';
import { TRIAL, STATE } from '@/constants/testIds/healthspan';

const labelize = (s) => (s || '').replace(/_/g, ' ').replace(/\b\w/g, c => c.toUpperCase());

export default function Trials() {
  const [meta, setMeta] = useState(null);
  const [mechanism, setMechanism] = useState('cellular_senescence');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => { api.get('/meta').then(r => setMeta(r.data)).catch(()=>{}); }, []);
  useEffect(() => {
    if (!mechanism) return;
    setLoading(true); setError(null);
    api.get(`/trials?mechanism=${mechanism}`)
      .then(r => setData(r.data))
      .catch(e => setError(e.response?.data?.detail || 'Failed to load'))
      .finally(() => setLoading(false));
  }, [mechanism]);

  return (
    <div className="space-y-6">
      <div>
        <div className="text-xs uppercase tracking-wider text-[color:var(--muted-fg)]">Clinical trials</div>
        <h1 className="mt-1 text-2xl sm:text-3xl font-semibold tracking-tight">Active studies by mechanism</h1>
        <p className="mt-1 text-sm text-[color:var(--muted-fg)] max-w-3xl">Records fetched live from clinicaltrials.gov v2 and cached daily. Every trial card links back to the canonical record on clinicaltrials.gov.</p>
      </div>

      <Card className="p-4 rounded-[var(--radius-md)] border border-[color:var(--border-color)] bg-[color:var(--surface)] grid sm:grid-cols-[280px_1fr] gap-3 items-end">
        <div className="space-y-1">
          <Label>Mechanism</Label>
          <Select value={mechanism} onValueChange={setMechanism}>
            <SelectTrigger data-testid={TRIAL.mechanismSelect}><SelectValue placeholder="Select mechanism" /></SelectTrigger>
            <SelectContent>
              {(meta?.mechanisms || []).map(m => <SelectItem key={m} value={m}>{labelize(m)}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
        <div className="text-xs text-[color:var(--muted-fg)] tabular-nums">{data?.cached_at && <>Cached at {new Date(data.cached_at).toLocaleString()} · {data.total} record{data.total === 1 ? '' : 's'}</>}</div>
      </Card>

      {loading && (
        <div data-testid={STATE.loading} className="grid md:grid-cols-2 gap-3">{[1,2,3,4].map(i => <Skeleton key={i} className="h-44" />)}</div>
      )}
      {error && (
        <Card data-testid={STATE.error} className="p-6 rounded-[var(--radius-md)] border border-[color:var(--danger)] bg-[color:var(--danger-bg)] text-[color:var(--danger)]">{error}</Card>
      )}
      {!loading && data?.trials?.length === 0 && (
        <Card data-testid={STATE.empty} className="p-8 rounded-[var(--radius-md)] border border-dashed border-[color:var(--border-color)] bg-[color:var(--surface)]">
          <div className="text-sm font-medium">No active trials returned</div>
          <p className="mt-1 text-sm text-[color:var(--muted-fg)]">Try a different mechanism or check back later.</p>
        </Card>
      )}

      <div className="grid md:grid-cols-2 gap-3">
        {data?.trials?.map(t => <TrialCard key={t.nct_id} trial={t} />)}
      </div>
    </div>
  );
}
